import { useEffect, useState } from "react";
import { useI18n, type MsgKey } from "../i18n";

const STAGE_KEYS = [
  "lessonDraftStageUpload",
  "lessonDraftStageExtract",
  "lessonDraftStageAnalyze",
  "lessonDraftStageDraft",
] as const satisfies MsgKey[];

// draftLesson() is a single blocking request with no server-reported progress,
// so these thresholds only drive a best-effort simulated stage indicator to
// keep the user oriented during the wait — they don't reflect real backend state.
const STAGE_THRESHOLDS_MS = [0, 2000, 6000, 14000];
const FRACTION_HORIZON_MS = 14000 + 8000;
const MAX_SIMULATED_FRACTION = 0.92;

export function lessonDraftProgress(startedAt: number, now = Date.now()) {
  const elapsedMs = Math.max(0, now - startedAt);
  const stageIndex = STAGE_THRESHOLDS_MS.reduce(
    (current, threshold, index) => (elapsedMs >= threshold ? index : current),
    0,
  );
  return {
    elapsedMs,
    elapsedSeconds: Math.floor(elapsedMs / 1000),
    stageIndex,
    stageKey: STAGE_KEYS[stageIndex] ?? STAGE_KEYS[0],
    fraction: Math.min(MAX_SIMULATED_FRACTION, elapsedMs / FRACTION_HORIZON_MS),
    stageKeys: STAGE_KEYS,
  };
}

export function LessonDraftingProgress({
  startedAt,
  compact = false,
}: {
  startedAt?: number;
  compact?: boolean;
}) {
  const { t } = useI18n();
  const [now, setNow] = useState(() => Date.now());
  const [fallbackStartedAt] = useState(() => Date.now());
  const origin = startedAt ?? fallbackStartedAt;

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, []);

  const { elapsedSeconds, stageIndex, stageKey, fraction, stageKeys } = lessonDraftProgress(origin, now);

  return (
    <div className={compact ? "space-y-2" : "space-y-3 rounded-md border border-brand-100 bg-brand-50/60 p-4"}>
      <div className={`flex items-center gap-2 text-sm ${compact ? "text-slate-600" : "font-medium text-brand-800"}`}>
        {!compact && (
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-brand-200 border-t-brand-600" />
        )}
        <span>{t(stageKey)}</span>
        <span className={`ml-auto text-xs font-normal ${compact ? "text-slate-500" : "text-brand-600"}`}>
          {t("lessonDraftElapsed", { seconds: String(elapsedSeconds) })}
        </span>
      </div>

      {!compact && (
        <div className="flex gap-1" role="list" aria-label={t("lessonGenerating")}>
          {stageKeys.map((key, index) => (
            <div
              key={key}
              role="listitem"
              aria-label={t(key)}
              aria-current={index === stageIndex ? "step" : undefined}
              className={`h-1.5 flex-1 rounded-full ${
                index < stageIndex ? "bg-emerald-500" : index === stageIndex ? "animate-pulse bg-brand-600" : "bg-slate-200"
              }`}
            />
          ))}
        </div>
      )}

      <div
        className="h-1 overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuenow={Math.round(fraction * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full rounded-full bg-brand-400 transition-[width] duration-500"
          style={{ width: `${Math.round(fraction * 100)}%` }}
        />
      </div>
    </div>
  );
}
